import type { QueryClient } from "@tanstack/react-query";
import { getTeacherUpcomingPrivateLessonsRange } from "@/lib/dashboard/teacher-dashboard-date-range";
import { DashboardWidgetId } from "@/lib/types/dashboard-widgets";
import { resolveEffectiveTenantNumber } from "@/lib/utils/env/effective-tenant";
import { prefetchPrivateLessonsCalendar } from "./prefetchPrivateLessonsCalendar";
import { prefetchTeacherClassEnrollments } from "./prefetchTeacherClassEnrollments";
import { prefetchTeacherClasses } from "./prefetchTeacherClasses";
import { prefetchWidgetSettings } from "./prefetchWidgetSettings";
import { getTeacherPrivateLessonsCalendarRange } from "@/lib/services/privateLessonsCalendarRange";

/** Server prefetch for teacher role on `/admin/dashboard`. */
export async function prefetchTeacherDashboard(
  queryClient: QueryClient,
  userId: number,
  accessToken: string,
  appointmentTeacherId: number = userId,
) {
  const tenantId = resolveEffectiveTenantNumber();

  const layouts = await prefetchWidgetSettings(
    queryClient,
    tenantId,
    accessToken,
  );
  const teacherLayout = layouts.Teacher;

  const { classIds } = await prefetchTeacherClasses(
    queryClient,
    userId,
    accessToken,
  );

  const tasks: Promise<unknown>[] = [];

  if (
    teacherLayout.regularWidgets.includes(
      DashboardWidgetId.RecentActivity,
    ) &&
    classIds.length > 0
  ) {
    tasks.push(
      prefetchTeacherClassEnrollments(queryClient, classIds, accessToken),
    );
  }

  const range7 = getTeacherUpcomingPrivateLessonsRange();
  tasks.push(
    prefetchPrivateLessonsCalendar(queryClient, accessToken, {
      startDate: range7.start,
      endDate: range7.end,
      teacherId: appointmentTeacherId,
    }),
  );

  if (
    teacherLayout.regularWidgets.includes(
      DashboardWidgetId.PrivateLessonsCalendar,
    )
  ) {
    const teacherCalendarRange = getTeacherPrivateLessonsCalendarRange();
    tasks.push(
      prefetchPrivateLessonsCalendar(queryClient, accessToken, {
        startDate: teacherCalendarRange.start,
        endDate: teacherCalendarRange.end,
        teacherId: appointmentTeacherId,
      }),
    );
  }

  await Promise.allSettled(tasks);

  return queryClient;
}
